import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type SearchSchemaInput,
} from "@tanstack/react-router";
import { z } from "zod";
import { AppShell } from "./components/layout/app-shell";
import { ErrorState } from "./components/ui/misc";
import { meQuery } from "./lib/session";
import { AuditPage } from "./routes/audit";
import { CategoriesPage } from "./routes/categories";
import { ContactPage } from "./routes/contact";
import { DashboardPage } from "./routes/dashboard";
import { ForgotPasswordPage } from "./routes/forgot-password";
import { LoginPage } from "./routes/login";
import { MediaPage } from "./routes/media";
import { PublishingPage } from "./routes/publishing";
import { ReportsPage } from "./routes/reports";
import { ResetPasswordPage } from "./routes/reset-password";
import { SettingsPage } from "./routes/settings";
import { UsersPage } from "./routes/users";
import { AnalyticsPage } from "./routes/analytics/overview";
import { GameAnalyticsPage } from "./routes/analytics/game";
import { GameEditorPage } from "./routes/games/editor";
import { GamesPage } from "./routes/games/list";

interface RouterContext {
  queryClient: QueryClient;
}

const page = z.coerce.number().int().min(1).catch(1);
const optionalText = z.string().min(1).optional().catch(undefined);
const analyticsSearch = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

/** Search params are validated on the way in and optional at every link site. */
const searchValidator =
  <TSchema extends z.ZodType>(schema: TSchema) =>
  (search: Partial<z.input<TSchema>> & SearchSchemaInput): z.output<TSchema> =>
    schema.parse(search);

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  errorComponent: ({ error }) => (
    <div className="p-6">
      <ErrorState error={error} />
    </div>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: searchValidator(z.object({ redirect: optionalText })),
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: searchValidator(z.object({ token: optionalText, error: optionalText })),
  component: ResetPasswordPage,
});

/** Every staff screen hangs off this route, so the session check happens exactly once. */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: "/login", search: { redirect: location.href } });
    return { me };
  },
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  component: DashboardPage,
});

const gamesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/games",
  validateSearch: searchValidator(
    z.object({
      page,
      q: optionalText,
      status: z.enum(["draft", "published", "archived"]).optional().catch(undefined),
      categoryId: z.coerce.number().int().positive().optional().catch(undefined),
      provider: z
        .enum(["gamedistribution", "external", "gamepush", "self_hosted"])
        .optional()
        .catch(undefined),
      sort: z.enum(["name", "sortOrder", "updatedAt", "createdAt"]).catch("updatedAt"),
      dir: z.enum(["asc", "desc"]).catch("desc"),
    }),
  ),
  component: GamesPage,
});

const gameCreateRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/games/new",
  component: GameEditorPage,
});

const gameEditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/games/$gameId",
  component: GameEditorPage,
});

const categoriesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/categories",
  component: CategoriesPage,
});

const mediaRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/media",
  validateSearch: searchValidator(
    z.object({
      page,
      q: optionalText,
      storage: z.enum(["r2", "external"]).optional().catch(undefined),
    }),
  ),
  component: MediaPage,
});

const publishingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/publishing",
  component: PublishingPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/reports",
  validateSearch: searchValidator(
    z.object({
      page,
      q: optionalText,
      status: z.enum(["new", "triaged", "resolved", "dismissed"]).optional().catch(undefined),
    }),
  ),
  component: ReportsPage,
});

const contactRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/contact",
  validateSearch: searchValidator(
    z.object({
      page,
      q: optionalText,
      status: z.enum(["new", "in_progress", "closed", "spam"]).optional().catch(undefined),
    }),
  ),
  component: ContactPage,
});

const usersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/users",
  validateSearch: searchValidator(
    z.object({
      page,
      q: optionalText,
      role: z.enum(["user", "support", "editor", "admin"]).optional().catch(undefined),
    }),
  ),
  component: UsersPage,
});

const analyticsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/analytics",
  validateSearch: searchValidator(analyticsSearch),
  component: AnalyticsPage,
});

const gameAnalyticsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/analytics/games/$gameId",
  validateSearch: searchValidator(analyticsSearch),
  component: GameAnalyticsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings",
  component: SettingsPage,
});

const auditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/audit",
  validateSearch: searchValidator(
    z.object({ page, entityType: optionalText, entityId: optionalText, action: optionalText }),
  ),
  component: AuditPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authedRoute.addChildren([
    dashboardRoute,
    gamesRoute,
    gameCreateRoute,
    gameEditRoute,
    categoriesRoute,
    mediaRoute,
    publishingRoute,
    reportsRoute,
    contactRoute,
    usersRoute,
    analyticsRoute,
    gameAnalyticsRoute,
    settingsRoute,
    auditRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
